import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useT } from '../hooks/useT';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PublicNav from '../components/layout/PublicNav';

export default function ResetPasswordPage() {
  const t = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({ new_password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  // Supabase embeds the session in the URL hash after the user clicks the reset link.
  // onAuthStateChange fires PASSWORD_RECOVERY once the session is loaded.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      setError(t('reset.mismatch'));
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: form.new_password });
      if (err) throw err;
      await supabase.auth.signOut();
      navigate('/login?reset=1');
    } catch (err) {
      setError(err.message || t('reset.invalid_token'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface">
      <PublicNav hideAuth />
      <div className="flex items-center justify-center p-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-100">{t('reset.title')}</h1>
          <p className="text-slate-400 mt-1">{t('reset.subtitle')}</p>
        </div>

        {!ready ? (
          <div className="card text-center space-y-4">
            <p className="text-red-400">{t('reset.invalid_token')}</p>
            <Link to="/forgot-password" className="text-brand-400 hover:text-brand-300 text-sm transition-colors">
              {t('reset.request_new')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="card space-y-4">
            <Input
              label={t('reset.password')}
              type="password"
              placeholder="••••••••"
              value={form.new_password}
              onChange={(e) => set('new_password', e.target.value)}
              required
              autoFocus
            />
            <Input
              label={t('reset.confirm')}
              type="password"
              placeholder="••••••••"
              value={form.confirm}
              onChange={(e) => set('confirm', e.target.value)}
              required
            />

            {error && (
              <div className="p-3 bg-red-900/20 border border-red-800 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" isLoading={loading}>
              {t('reset.submit')}
            </Button>

            <p className="text-center text-sm text-slate-500">
              <Link to="/login" className="text-brand-400 hover:text-brand-300 transition-colors">
                ← {t('forgot.back_login')}
              </Link>
            </p>
          </form>
        )}
      </div>
      </div>
    </div>
  );
}
